/**
 * Sous-titre du planning v2 (SPEC PL7, PL10). Fonctions pures.
 *
 * Revue du 05/10 : avec 2 studios ou plus, le sous-titre revenait à « Tous les studios Dynamo, RIISE et Punch » alors
 * que la liste affichait « 2 studios ». Le sous-titre suit tous les filtres de lieu et de coach. Plus de légende des
 * marques depuis les retours du 07/10 (une seule marque affichée à la fois, son nom reste sur chaque carte).
 */
import { siteOfKey, type FilterOption, type PlanningFilters } from './filters';

/** « Dynamo, RIISE et Punch ». */
export const joinNames = (names: string[]) =>
  names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} et ${names[names.length - 1]}`;

export interface SubtitleInput {
  filters: PlanningFilters;
  /** Vue multi-marques (filtre Marque, étiquettes de marque). */
  showBrands: boolean;
  /** Marques affichées, dans l'ordre des sites. */
  brands: { siteId: string; name: string }[];
  /** Options des listes Studios / Coachs (toutes marques affichées). */
  studioOptions: FilterOption[];
  coachOptions: FilterOption[];
  /** Coach figé du mode coach (jamais repris dans le sous-titre : le titre le nomme). */
  lockedCoachKey: string | null;
}

/** Noms de marque, dans l'ordre des sites, des clés « site:id » données. */
function brandsOfKeys(keys: string[], brands: SubtitleInput['brands']): string[] {
  const sites = new Set(keys.map(siteOfKey));
  return brands.filter((brand) => sites.has(brand.siteId)).map((brand) => brand.name).filter(Boolean);
}

/**
 * « Tous les studios Dynamo, RIISE et Punch », « Tous les studios RIISE », « Boétie · Dynamo », « 2 studios · Dynamo »,
 * « 3 studios · Dynamo et RIISE » ; puis le coach choisi (« · Cynthia ») ou leur nombre (« · 2 coachs »).
 * Espaces insécables autour des points médians (jamais un « · » en début de ligne).
 * null : un studio ou un coach choisi dont le nom n'est pas encore connu (listes en chargement) — le sous-titre attend
 * (jamais « 1 studio » puis « Oberkampf » sous les yeux, revue du 05/10).
 */
export function planningSubtitle({
  filters,
  showBrands,
  brands,
  studioOptions,
  coachOptions,
  lockedCoachKey,
}: SubtitleInput): string | null {
  const labelOf = (siteId: string) => brands.find((brand) => brand.siteId === siteId)?.name ?? '';
  const studios = filters.studios;
  let place: string;
  if (studios.length === 1) {
    const option = studioOptions.find((o) => o.value === studios[0]);
    if (!option?.label) return null;
    const name = option.label;
    const brand = showBrands ? labelOf(siteOfKey(studios[0])) : '';
    place = brand ? `${name}\u00a0· ${brand}` : name;
  } else if (studios.length > 1) {
    const names = showBrands ? joinNames(brandsOfKeys(studios, brands)) : '';
    place = names ? `${studios.length} studios\u00a0· ${names}` : `${studios.length} studios`;
  } else if (showBrands) {
    place = filters.brand
      ? `Tous les studios ${labelOf(filters.brand)}`.trim()
      : `Tous les studios ${joinNames(brands.map((brand) => brand.name).filter(Boolean))}`.trim();
  } else {
    place = 'Tous les studios';
  }

  const coaches = filters.coaches.filter((key) => key !== lockedCoachKey);
  if (coaches.length === 1) {
    const coach = coachOptions.find((o) => o.value === coaches[0])?.label;
    return coach ? `${place}\u00a0· ${coach}` : null;
  }
  if (coaches.length > 1) return `${place}\u00a0· ${coaches.length} coachs`;
  return place;
}
